import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'
import { isStaticAssetPath } from '@/utils/staticAssetPath'

function requireAuth(to, from, next) {
  if (localStorage.getItem('gs_admin_auth') === 'true') {
    next();
  } else {
    next({ name: 'admin-login' });
  }
}

const routes = [
  {
    path: '/',
    name: 'home',
    meta: {
      loadActions: ['loadHeroSlides', 'loadEstudios', 'loadPrensa', 'loadCasos'],
    },
    component: () => import(/* webpackChunkName: "home" */ '../views/HomeView.vue')
  },
  {
    path: '/sobre-gs',
    name: 'sobre-gs',
    meta: { loadActions: ['loadCasos'] },
    component: () => import(/* webpackChunkName: "sobre-gs" */ '../views/SobreGsView.vue')
  },
  {
    path: '/equipo',
    name: 'equipo',
    meta: { loadActions: ['loadEquipo'] },
    component: () => import(/* webpackChunkName: "equipo" */ '../views/EquipoView.vue')
  },
  {
    path: '/nuestro-contenido',
    name: 'nuestro-contenido',
    meta: {
      loadActions: ['loadEstudios', 'loadPrensa', 'loadPodcasts', 'loadEventos'],
    },
    component: () => import(/* webpackChunkName: "nuestro-contenido" */ '../views/NuestroContenidoView.vue')
  },
  {
    path: '/gs-tech-lab',
    name: 'gs-tech-lab',
    component: () => import(/* webpackChunkName: "gs-tech-lab" */ '../views/GsTechLabView.vue')
  },
  {
    path: '/division-sur',
    name: 'division-sur',
    component: () => import(/* webpackChunkName: "division-sur" */ '../views/DivisionSurView.vue')
  },
  {
    path: '/contacto',
    name: 'contacto',
    component: () => import(/* webpackChunkName: "contacto" */ '../views/ContactView.vue')
  },
  {
    path: '/nuestro-contenido/casos-destacados',
    name: 'casos-destacados',
    meta: { loadActions: ['loadCasos'] },
    component: () => import(/* webpackChunkName: "casos-destacados" */ '../pages/CasosDestacadosPage.vue')
  },
  {
    path: '/nuestro-contenido/estudios',
    name: 'estudios',
    meta: { loadActions: ['loadEstudios'] },
    component: () => import(/* webpackChunkName: "estudios" */ '../pages/EstudioListPage.vue')
  },
  {
    path: '/nuestro-contenido/prensa',
    name: 'prensa',
    meta: { loadActions: ['loadPrensa'] },
    component: () => import(/* webpackChunkName: "prensa" */ '../pages/PrensaListPage.vue')
  },
  {
    path: '/prensa/:slug',
    name: 'prensa-articulo',
    meta: { loadActions: ['loadPrensa'] },
    component: () => import(/* webpackChunkName: "prensa-articulo" */ '../pages/PrensaPage.vue'),
    async beforeEnter(to) {
      const slug = String(to.params.slug ?? '').trim()
      const exists = store.getters.prensaBySlug(slug)
      if (exists) return true
      return { name: 'prensa' }
    },
  },
  {
    path: '/admin/login',
    name: 'admin-login',
    meta: { isAdmin: true },
    component: () => import(/* webpackChunkName: "admin-login" */ '../views/admin/AdminLoginView.vue'),
  },
  {
    path: '/admin',
    component: () => import(/* webpackChunkName: "admin-layout" */ '../views/admin/AdminLayout.vue'),
    meta: { isAdmin: true },
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: { name: 'admin-collection', params: { collection: 'banners' } },
      },
      {
        path: ':collection',
        name: 'admin-collection',
        meta: { isAdmin: true },
        component: () => import(/* webpackChunkName: "admin-list" */ '../views/admin/AdminCollectionList.vue'),
      },
      {
        path: ':collection/new',
        name: 'admin-new',
        meta: { isAdmin: true },
        component: () => import(/* webpackChunkName: "admin-form" */ '../views/admin/AdminCollectionForm.vue'),
      },
      {
        path: ':collection/:id/edit',
        name: 'admin-edit',
        meta: { isAdmin: true },
        component: () => import(/* webpackChunkName: "admin-form" */ '../views/admin/AdminCollectionForm.vue'),
      },
    ],
  },
  {
    path: '/404',
    name: 'not-found',
    component: () => import(/* webpackChunkName: "not-found" */ '../views/NotFoundView.vue'),
  },
  {
    path: '/:slug',
    name: 'caso-destacado',
    meta: { loadActions: ['loadCasos'] },
    component: () => import(/* webpackChunkName: "caso-destacado" */ '../pages/CasoPage.vue'),
    async beforeEnter(to) {
      const slug = String(to.params.slug ?? '').trim()
      if (isStaticAssetPath(`/${slug}`)) {
        window.location.replace(to.fullPath)
        return false
      }
      const exists = store.getters.casoByLink(slug)
      if (exists) return true
      return { name: 'not-found', replace: true }
    },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: () => ({ name: 'not-found', replace: true }),
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    }

    if (savedPosition) {
      return savedPosition
    }

    return { top: 0, left: 0, behavior: 'instant' }
  },
})

function getRouteLoadActions(to) {
  return [...new Set(
    to.matched.flatMap((record) => record.meta?.loadActions ?? []),
  )]
}

/**
 * Solo la primera acción bloquea la navegación (para que el hero cargue ASAP).
 * Las demás se difieren al idle del navegador, no awaited.
 */
export function splitLoadActions(to) {
  const actions = getRouteLoadActions(to)
  return {
    blocking: actions.slice(0, 1),
    deferred: actions.slice(1),
  }
}

/**
 * Despacha las acciones diferidas en `requestIdleCallback` (o fallback setTimeout).
 * No awaited: el loader se descartó al terminar las blocking.
 */
export function scheduleIdleDispatch(actions, dispatch, fallbackMs = 1500) {
  if (!actions?.length) return
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => actions.forEach(dispatch))
    return
  }
  setTimeout(() => actions.forEach(dispatch), fallbackMs)
}

router.beforeEach(async (to) => {
  if (isStaticAssetPath(to.path)) {
    window.location.replace(to.fullPath)
    return false
  }

  if (to.meta?.isAdmin) {
    store.commit('SET_SITE_LOADER', false)
    return true
  }

  store.commit('SET_SITE_LOADER', true)

  const { blocking, deferred } = splitLoadActions(to)
  if (blocking.length) {
    await Promise.all(blocking.map((action) => store.dispatch(action)))
  }

  scheduleIdleDispatch(deferred, (action) => {
    store.dispatch(action)
  })

  return true
})

export default router